import { useState, useCallback, useMemo, useRef } from 'react';

/**
 * Derives a display name for a conversation participant.
 * Handles all user types: business users, creators, global users
 */
const getDisplayName = (participant) => {
  if (!participant) return 'Unknown';
  
  // Try first_name + last_name (business users)
  const firstName = participant.first_name || '';
  const lastName = participant.last_name || '';
  if (firstName || lastName) return `${firstName}${lastName ? ' ' + lastName : ''}`.trim();
  
  // Try display_name
  if (participant.display_name) return participant.display_name;
  
  // Try name (creators or other types)
  if (participant.name) return participant.name;
  
  // Try username
  if (participant.username) return participant.username;
  
  // Try creator-specific fields
  if (participant.creator_name) return participant.creator_name;
  if (participant.creator_username) return participant.creator_username;
  if (participant.creator_id) return String(participant.creator_id);
  
  // Try global_user_id as fallback
  if (participant.global_user_id) return String(participant.global_user_id);
  
  // Try id
  if (participant.id) return String(participant.id);
  
  return 'Unknown';
};

/**
 * Returns a unique identifier for a participant.
 * Tries multiple ID fields to handle all user types.
 */
const getParticipantId = (participant) => {
  if (!participant) return null;
  
  // Priority order for ID fields
  if (participant.global_user_id) return String(participant.global_user_id);
  if (participant.id) return String(participant.id);
  if (participant.user_id) return String(participant.user_id);
  if (participant.creator_id) return String(participant.creator_id);
  if (participant.username) return participant.username;
  
  return null;
};

/**
 * Returns a mention tag string for insertion into message text.
 * Returns just the identifier (username or displayName) without @ prefix
 */
const getMentionTag = (participant) => {
  if (!participant) return '';
  
  const username = participant.username;
  const displayName = getDisplayName(participant);
  
  if (username) {
    return username;
  }
  
  if (participant.creator_id) {
    return String(participant.creator_id);
  }
  
  if (participant.global_user_id) {
    return String(participant.global_user_id);
  }
  
  if (participant.user_id) {
    return String(participant.user_id);
  }
  
  return displayName || '';
};

const MAX_SUGGESTIONS = 8;
const MENTION_TRIGGER_REGEX = /(^|\s)@("[^"]*"|\S*)$/;

/**
 * Hook for @mention functionality in chat inputs.
 * @param {Array} participants - Array of conversation participants
 * @param {string|number} currentUserId - ID of the current user (to exclude from suggestions)
 * @returns mention state and handlers
 */
export default function useMentions(participants, currentUserId) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const inputRef = useRef(null);

  const filteredParticipants = useMemo(() => {
    if (!participants?.length) {
      return [];
    }
    const q = query.toLowerCase();

    // If no search query, show all participants (including self for self-tagging)
    if (!q) {
      return participants.slice(0, MAX_SUGGESTIONS);
    }

    // Search with filtering
    return participants
      .filter((p) => {
        // Get all possible IDs for this participant - normalize to string and trim
        const participantIds = [
          p.id ? String(p.id).trim() : null,
          p.global_user_id ? String(p.global_user_id).trim() : null,
          p.user_id ? String(p.user_id).trim() : null,
          p.creator_id ? String(p.creator_id).trim() : null,
          p.username || null,
        ].filter(Boolean);

        // Search across all display name and ID fields
        const name = getDisplayName(p).toLowerCase();
        const username = (p.username || '').toLowerCase();
        const creatorId = (p.creator_id ? String(p.creator_id) : '').toLowerCase();
        const globalUserId = (p.global_user_id ? String(p.global_user_id) : '').toLowerCase();
        const userId = (p.user_id ? String(p.user_id) : '').toLowerCase();
        const id = (p.id ? String(p.id) : '').toLowerCase();
        
        return name.includes(q) || username.includes(q) || creatorId.includes(q) || 
               globalUserId.includes(q) || userId.includes(q) || id.includes(q);
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [participants, query]);

  const isVisible = showSuggestions && filteredParticipants.length > 0;

  const closeSuggestions = useCallback(() => {
    setShowSuggestions(false);
    setQuery('');
    setSelectedIndex(0);
    setMentionStart(-1);
  }, []);

  /**
   * Call on every input change to detect @mention queries.
   * @param {string} text - current input value
   * @param {number} cursorPos - current cursor position (selectionStart)
   */
  const onTextChange = useCallback(
    (text, cursorPos) => {
      if (cursorPos == null) {
        closeSuggestions();
        return;
      }
      const before = text.substring(0, cursorPos);
      const match = before.match(MENTION_TRIGGER_REGEX);
      if (match) {
        let queryText = match[2];
        if (queryText.startsWith('"') && queryText.endsWith('"')) {
          queryText = queryText.slice(1, -1);
        }
        setQuery(queryText.toLowerCase());
        setMentionStart(before.length - match[2].length - 1);
        setShowSuggestions(true);
        setSelectedIndex(0);
      } else {
        closeSuggestions();
      }
    },
    [closeSuggestions]
  );

  const moveUp = useCallback(() => {
    setSelectedIndex((prev) => (prev <= 0 ? filteredParticipants.length - 1 : prev - 1));
  }, [filteredParticipants.length]);

  const moveDown = useCallback(() => {
    setSelectedIndex((prev) => (prev >= filteredParticipants.length - 1 ? 0 : prev + 1));
  }, [filteredParticipants.length]);

  /**
   * Insert a selected mention into the message text.
   * Returns the new message string. Caller must call setMessage() with it.
   */
  const insertMention = useCallback(
    (participant, currentMessage) => {
      const tag = getMentionTag(participant);
      const before = currentMessage.substring(0, mentionStart);

      let endPos = mentionStart + 1 + query.length;
      while (endPos < currentMessage.length && !/\s/.test(currentMessage[endPos])) {
        endPos++;
      }
      const after = currentMessage.substring(endPos);
      
      let newMessage;
      let newCursorPos;
      
      const hasSpaces = tag.includes(' ');
      if (hasSpaces) {
        newMessage = `${before}"@${tag}" ${after}`;
        newCursorPos = mentionStart + tag.length + 4;
      } else {
        newMessage = `${before}@${tag} ${after}`;
        newCursorPos = mentionStart + tag.length + 2;
      }

      closeSuggestions();

      setTimeout(() => {
        const el = inputRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);

      return newMessage;
    },
    [mentionStart, query, closeSuggestions]
  );

  return {
    isVisible,
    filteredParticipants,
    selectedIndex,
    onTextChange,
    closeSuggestions,
    moveUp,
    moveDown,
    insertMention,
    inputRef,
    getDisplayName,
    getMentionTag,
  };
}

export { getDisplayName, getMentionTag };
